import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, FileText, CheckCircle2, ShieldCheck } from "lucide-react";
import { SERVICES, CITIES } from "../data/content";
import { PRICING, CORE_CITIES, OUTER_TRAVEL_FEE, DEPOSIT_AMOUNT } from "../config/pricing";
import { SITE } from "../config/site";
import { PageHero, Reveal, useSeo } from "../components/Reveal";
import { PayPalBox } from "../components/PayPalBox";

const inputCls =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-teal-600 focus:ring-2 focus:ring-teal-500/40";

export default function Payments() {
  useSeo(
    "Pay Online | SwiftTest Solutions — Eastern Iowa",
    "Pay a booking deposit, pay for a scheduled mobile testing visit, or pay a SwiftTest invoice securely online with PayPal or card."
  );
  const [params] = useSearchParams();

  const [service, setService] = useState(params.get("service") || "");
  const [headcount, setHeadcount] = useState(Number(params.get("headcount")) || 5);
  const [city, setCity] = useState(params.get("city") || "");
  const [mode, setMode] = useState(params.get("deposit") === "1" ? "deposit" : "full");

  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceNote, setInvoiceNote] = useState("");

  const [paid, setPaid] = useState(null);

  const bookingTotal = useMemo(() => {
    const rate = PRICING.services[service];
    if (!rate) return null;
    const travel = city && !CORE_CITIES.includes(city) ? OUTER_TRAVEL_FEE : 0;
    return rate.base + rate.perPerson * headcount + travel;
  }, [service, headcount, city]);

  const payAmount = mode === "deposit" ? DEPOSIT_AMOUNT : bookingTotal;
  const invoiceValue = parseFloat(invoiceAmount);
  const invoiceValid = !isNaN(invoiceValue) && invoiceValue >= 5 && invoiceValue <= 10000;

  return (
    <div data-testid="payments-page">
      <PageHero
        testId="payments-hero"
        eyebrow="Pay online"
        title="Fast, secure payment — no paperwork"
        lead={`Reserve a visit with a deposit, pay for a scheduled service in full, or settle an invoice. Cards and PayPal accepted. Questions? Call or text ${SITE.phoneDisplay}.`}
      />

      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-5 pb-24 pt-14 sm:px-8">
          {paid && (
            <Reveal>
              <div
                data-testid="payment-success-banner"
                className="mb-10 flex items-start gap-4 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <h2 className="font-display text-xl font-bold text-emerald-950">
                    Payment received — ${Number(paid.amount).toFixed(2)}
                  </h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-emerald-900/80">
                    Thank you! Reference <strong>{paid.payment_id.slice(0, 8).toUpperCase()}</strong>.
                    {paid.payer_notified
                      ? " A confirmation email is on its way to your PayPal email address."
                      : ""}{" "}
                    Our team will confirm your appointment or apply your payment right away. For
                    anything urgent, call or text {SITE.phoneDisplay}.
                  </p>
                </div>
              </div>
            </Reveal>
          )}

          <div className="grid items-start gap-8 lg:grid-cols-2">
            {/* BOOK & PAY */}
            <Reveal>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                    <CalendarCheck className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-xl font-bold text-slate-900">Book &amp; pay for a visit</h2>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-2 rounded-full bg-slate-100 p-1.5">
                  <button
                    data-testid="pay-mode-full"
                    onClick={() => setMode("full")}
                    className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all ${
                      mode === "full" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    Pay in full
                  </button>
                  <button
                    data-testid="pay-mode-deposit"
                    onClick={() => setMode("deposit")}
                    className={`rounded-full px-4 py-2.5 text-sm font-bold transition-all ${
                      mode === "deposit" ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    ${DEPOSIT_AMOUNT} deposit
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  <select
                    data-testid="pay-service-select"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className={inputCls}
                  >
                    <option value="" disabled>Service *</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                  </select>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        People to test
                      </span>
                      <span className="rounded-full bg-teal-700 px-3 py-1 font-display text-sm font-bold text-white">
                        {headcount}
                      </span>
                    </div>
                    <input
                      data-testid="pay-headcount-slider"
                      type="range"
                      min="1"
                      max="50"
                      value={headcount}
                      onChange={(e) => setHeadcount(Number(e.target.value))}
                      className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-teal-700"
                    />
                  </div>

                  <select
                    data-testid="pay-city-select"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">City (optional)</option>
                    {CITIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-6 flex items-end justify-between rounded-2xl bg-slate-900 px-5 py-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    {mode === "deposit" ? "Deposit due today" : "Total due today"}
                  </span>
                  <span data-testid="pay-amount" className="font-display text-3xl font-bold text-white">
                    {payAmount ? `$${payAmount}` : "—"}
                  </span>
                </div>

                <div className="mt-5">
                  <PayPalBox
                    testId="paypal-booking-buttons"
                    disabled={!service}
                    buildPayload={() => ({
                      kind: mode === "deposit" ? "deposit" : "booking",
                      service,
                      headcount,
                      city,
                    })}
                    onPaid={setPaid}
                  />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
                  Ballpark pricing — your final quote is confirmed by our team. Deposits are applied
                  to your final invoice.
                </p>
              </div>
            </Reveal>

            {/* INVOICE */}
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-xl font-bold text-slate-900">Pay an invoice</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Received a quote or invoice from us? Enter the amount and a reference so we can
                  match your payment instantly.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-lg font-bold text-slate-400">
                      $
                    </span>
                    <input
                      data-testid="invoice-amount-input"
                      type="number"
                      min="5"
                      max="10000"
                      step="0.01"
                      value={invoiceAmount}
                      onChange={(e) => setInvoiceAmount(e.target.value)}
                      placeholder="Amount (USD)"
                      className={`${inputCls} pl-9 font-display text-lg font-bold`}
                    />
                  </div>
                  <input
                    data-testid="invoice-note-input"
                    value={invoiceNote}
                    onChange={(e) => setInvoiceNote(e.target.value)}
                    placeholder="Invoice # or company name (helps us match it)"
                    className={inputCls}
                  />
                </div>

                <div className="mt-6">
                  <PayPalBox
                    testId="paypal-invoice-buttons"
                    disabled={!invoiceValid}
                    buildPayload={() => ({
                      kind: "invoice",
                      amount: invoiceValue,
                      note: invoiceNote,
                    })}
                    onPaid={setPaid}
                  />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
                  Minimum $5. Your card statement will show PayPal.
                </p>
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-2xl border border-teal-200 bg-teal-50 p-5">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" />
                <p className="text-xs leading-relaxed text-teal-900/80">
                  Payments are processed securely by PayPal — SwiftTest never sees or stores your
                  card details. Prefer not to pay online? Call or text {SITE.phoneDisplay} and we'll
                  take care of it.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
